/* System defined core library */
import { registerLocaleData } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, HostListener, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Event, NavigationEnd, NavigationError, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as $ from 'jquery';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { map, filter, mergeMap } from 'rxjs/operators';
import { clearTimeout, setTimeout } from 'timers';

/* User defined library/components */
import { BootstrapParams } from '../bootstrap-params';
import { MetricApiService } from './scheduling/shared/services/metric-api.service';
import { OrchestrationApiService } from './scheduling/shared/services/orchestration-api.service';
import { SchedulingApiService } from './scheduling/shared/services/scheduling-api.service';
import { SelectedExamDetailService } from './scheduling/shared/services/selected-exam-detail.service';
import { CultureService } from './shared/services/culture.service';
import { DeepLinkingService } from './shared/services/deep-linking.service';
import { UtilityService } from './shared/services/utility.service';
import { LoaderService } from './shared/services/loader.service';

/* User defined models */
import { Dictionary } from './shared/models/common/shared.model';
import { Candidate } from './shared/models/scheduling/candidate.model';
import { Client } from './shared/models/scheduling/client.model';
import { Hold } from './shared/models/scheduling/hold.model';
import { Program } from './shared/models/scheduling/program.model';

/* User defined enums/constants */
import * as constants from './shared/constants/scheduling.constant';
import { InputType, LogLevel, NavigationAction } from './shared/enums/scheduling.enum';

/* Languages */
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localeCy from '@angular/common/locales/cy';
import localeGd from '@angular/common/locales/gd';
import localeMs from '@angular/common/locales/ms';
import localeId from '@angular/common/locales/id';
import localeRo from '@angular/common/locales/ro';
import localeNl from '@angular/common/locales/nl';
import localeEl from '@angular/common/locales/el';
import localeDa from '@angular/common/locales/da';
import localePl from '@angular/common/locales/pl';
import localePtPT from '@angular/common/locales/pt-PT';
import localeHr from '@angular/common/locales/hr';
import localeSl from '@angular/common/locales/sl';
import localeHu from '@angular/common/locales/hu';
import localeJa from '@angular/common/locales/ja';
import localeZh from '@angular/common/locales/zh';
import localeZhHant from '@angular/common/locales/zh-Hant';
import localeKo from '@angular/common/locales/ko';

declare var window: any;

/*Handles the event fired when the window, the document, and its resources are about to be unloaded.
 * Gives a browser popup when the user is about to leave the page.
 * To implement extend AppComponent with this class.*/
abstract class BeforeunloadClass {
  public hold: Hold;
  public holds: Hold[];
  public client: Client;
  public program: Program;

  public targetFlow: string;
  public confirmationNumber: string;
  public postedConfirmationNumber: string;

  constructor(protected selectedExamDetailService: SelectedExamDetailService, protected metricsApiService: MetricApiService) {
    selectedExamDetailService.hold.subscribe((hold => {
      this.hold = hold;
    }));

    selectedExamDetailService.holds.subscribe((holds => {
      this.holds = holds;
    }));

    selectedExamDetailService.client.subscribe((client => {
      this.client = client;
    }));


    selectedExamDetailService.program.subscribe((program => {
      this.program = program;
    }));
  }

  showPopup() {
    var ua = window.navigator.userAgent;
    var msie = ua.indexOf("MSIE ");

    if (msie > 0 || navigator.userAgent.match(/Trident.*rv\:11\./)) {
      event.preventDefault();
      return true;
    }
    else {
      return false;
    }
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['../../Assets/scss/main.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent extends BeforeunloadClass {

  /* User defined private/public variables */
  private sessionTime: string;
  public browserCulture = window.navigator.language;

  /* Input-Output parameters declarations */
  languageCode$: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private bootstrapParams: BootstrapParams,
    private cultureService: CultureService,
    private deepLinkingService: DeepLinkingService,
    private httpService: HttpClient,
    private orchestrationApiService: OrchestrationApiService,
    private router: Router,
    private schedulingApiService: SchedulingApiService,
    private titleService: Title,
    private translate: TranslateService,
    private utilityService: UtilityService,
    private loaderService: LoaderService,
    metricsApiService: MetricApiService,
    selectedExamDetailService: SelectedExamDetailService
  ) {
    // Uncomment to implement BeforeunloadClass
    super(selectedExamDetailService, metricsApiService);

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    translate.use(this.selectedExamDetailService.selectedLanguageCode);
    router.events.pipe(filter(event => (event instanceof NavigationEnd || event instanceof NavigationError))).subscribe((event: Event) => {
      window.scrollTo(0, 0);
    });
    this.sessionTime = this.bootstrapParams.SessionTime;
    this.selectedExamDetailService.clientCode = this.bootstrapParams.clientCode;


    this.languageCode$ = selectedExamDetailService.languageCode.subscribe(
      lang => {
        // sets language attribute on <html> tag
        $('html').attr('lang', this.cultureService.mapLanguageCodeToISO(lang));
      });
  }

  ngOnInit() {
    this.loaderService.show();
    this.metricsApiService.log(LogLevel.Info, `User Agent: ${window.navigator.userAgent}`);

    this.idleTimer(
      this.router,
      this.selectedExamDetailService,
      this.sessionTime,
      this.schedulingApiService,
      this.bootstrapParams,
      this.utilityService,
      this.metricsApiService
    );

    this.selectedExamDetailService.browserCulture = this.browserCulture;

    registerLocaleData(localeDe);
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);
    registerLocaleData(localeIt);
    registerLocaleData(localeCy);
    registerLocaleData(localeGd);
    registerLocaleData(localeMs);
    registerLocaleData(localeId);
    registerLocaleData(localeRo);
    registerLocaleData(localeNl);
    registerLocaleData(localeEl);
    registerLocaleData(localeDa);
    registerLocaleData(localePl);
    registerLocaleData(localePtPT);
    registerLocaleData(localeHr);
    registerLocaleData(localeSl);
    registerLocaleData(localeHu);
    registerLocaleData(localeJa);
    registerLocaleData(localeZh);
    registerLocaleData(localeZhHant);
    registerLocaleData(localeKo);

    /*Reading and setting document title*/
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) route = route.firstChild;
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((event) => {
        const seprator = ' - ';
        let title = 'ProScheduler';
        if (event['title'] !== null && event['title'] !== undefined) {
          title += seprator + event['title'];
        }
        this.titleService.setTitle(title);
      });
    this.selectedExamDetailService.lpiCandidateGuid = this.bootstrapParams.candidateGuid;

    if (this.bootstrapParams && this.bootstrapParams.LpiParams !== '') {

      let lpiParams = this.bootstrapParams.LpiParams;
      lpiParams = !!lpiParams ? $("<textarea/>").html(lpiParams).text() : lpiParams;
      let LPIParams = lpiParams.split('&');
      let processedParams: any = {};

      LPIParams.forEach((param) => {
        if (param.indexOf("candDemo") > -1) {
          this.loadXml(param);
        }
        var params = param.split('=');
        processedParams[params[0]] = decodeURIComponent(params[1]);
      });
      this.deepLinkingService.processParams(processedParams).then((deepLinkFailed) => {
        if (!deepLinkFailed) {
          this.loaderService.hide();
          this.router.navigate(['/home']);
        }
      })
    } else {
      this.activatedRoute.queryParams.subscribe((params: Params) => {
        if (Object.keys(params).length === 0 && params.constructor === Object) {
          this.loaderService.hide();
          return;
        }
        this.deepLinkingService.processParams(params).then((deepLinkFailed) => {
          if (!deepLinkFailed) {
            this.loaderService.hide();
            this.router.navigate(['/home']);
          }
        })
      });
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (window.ga && ga.create) {
          window.ga('send', 'pageview');
        }
      }
    });


    // geo-encoding url based on web.config setting
    let geoEncodingUrl: string;
    if (this.bootstrapParams.useBingMaps) {
      geoEncodingUrl = constants.BingLocationApiURL + `?q=Baltimore&key=${this.bootstrapParams.bingMapKey}&maxResults=1`;
    } else {
      let queryParams: HttpParams = new HttpParams().set('v', '3.exp').set('key', this.bootstrapParams.googleMapKey).set('address', 'Baltimore');
      geoEncodingUrl = constants.GoogleApiURL + '?' + queryParams;
    }

    // geo-encoding url check, if error, set isGoogleMapsValid to false
    this.httpService.get(geoEncodingUrl)
      .subscribe({
        next: (data: any) => {
          if (!data) {
            this.selectedExamDetailService.isGoogleMapsValid = false;
          }
          if (data && (!!data.error_message)) {
            this.selectedExamDetailService.isGoogleMapsValid = false;
          }
          if (data && data.results && data.results.length && !data.results[0]['address_components']) {
            this.selectedExamDetailService.isGoogleMapsValid = false;
          } else {
            this.metricsApiService.log(LogLevel.Info, "Map Provider is valid");
          }
        },
        error: (err) => {
          this.selectedExamDetailService.isGoogleMapsValid = false;
          this.metricsApiService.log(LogLevel.Info, "Map Provider is invalid");
        }
      });

    if (this.browserCulture) {
      this.orchestrationApiService.getDateTimeInfo(this.browserCulture).subscribe((res: any) => {
        if (res && res.Results) {
          this.selectedExamDetailService.dateFormat = res.Results.Format;
          this.selectedExamDetailService.timeFormat = res.Results.Use24Hour;
        }
      })
    }
  }

  navigateToNext(): void {
    this.router.navigate(['/scheduling/findAvailability']);
  }

  loadXml(demographicXml: string) {
    let xml = demographicXml.replace("candDemo=", "");
    if (xml.indexOf("+") > -1) {
      xml = xml.replace(/\+name/g, " name");
    }
    const xmlDoc = $.parseXML(xml);
    const demographicElm = xmlDoc.getElementsByTagName('demographic');
    let tempCandidate = new Candidate();
    tempCandidate.defaultDetails = new Dictionary();
    for (let i = 0; i < demographicElm.length; i++) {
      tempCandidate.defaultDetails.add(demographicElm[i].getAttribute('name').toLowerCase(), demographicElm[i].childNodes[0].nodeValue);
    }
    this.selectedExamDetailService.setLpiCandidateSource(tempCandidate);
  }

  idleTimer(
    router: Router,
    selectedExamDetailSvc: SelectedExamDetailService,
    sessionTime: string,
    schedulingApiSvc: SchedulingApiService,
    bootstrapParams: BootstrapParams,
    utilitySvc: UtilityService,
    metricApiSvc: MetricApiService
  ) {
    var t: any;

    var expireLogMessage = 'Session Timer Expired: User Redirected to home page.';

    var hold: Hold;
    selectedExamDetailSvc.hold.subscribe(
      _hold => {
        hold = _hold;
      });

    var holds: Hold[];
    selectedExamDetailSvc.holds.subscribe(
      _holds => {
        holds = _holds;
      }
    );

    window.addEventListener('click', throttleResetTimer, false);
    window.addEventListener('keydown', throttleResetTimer, false);
    window.addEventListener('scroll', throttleResetTimer, false);
    window.addEventListener('mousemove', throttleResetTimer, false);
    window.addEventListener('mousedown', throttleResetTimer, false);
    window.addEventListener('load', throttleResetTimer, false);

    function reload() {
      //if (window.location.pathname !== '/home' && window.location.pathname !== '/') {
      window.addEventListener('click', goHome, false);
      window.addEventListener('keydown', goHome, false);
      //}
    }

    function goHome() {
      window.removeEventListener('click', goHome, false);
      window.removeEventListener('keydown', goHome, false);
      if (window.location.pathname !== '/home' && window.location.pathname !== '/') {
        if (!!holds && holds.length > 1 && !selectedExamDetailSvc.isFlowComplete) {
          holds.forEach((h: Hold) => {
            schedulingApiSvc.releaseHold(h).subscribe(() => { });
          });
        } else if (hold && hold.id && !selectedExamDetailSvc.isFlowComplete) {
          schedulingApiSvc.releaseHold(hold).subscribe(res => {
          });
        }

        const programCodes: string[] = ['DTT', 'ESDS', 'CPC', 'ADI']; // add them in the array

        let doesPrgNeedRefresh = programCodes.some(elm => elm === (selectedExamDetailSvc.programCode ? selectedExamDetailSvc.programCode.toUpperCase() : ''));

        metricApiSvc.log(LogLevel.Debug, expireLogMessage);
        if (doesPrgNeedRefresh) {
          window.location.reload();
        } else {
          selectedExamDetailSvc.resetExamService();
          router.navigate(['/home']);
        }

        selectedExamDetailSvc.sessionExpired = true;
      }
    }

    function refresh() {
      window.removeEventListener('click', refresh, false);
      window.removeEventListener('keydown', refresh, false);
      var now = moment();
      var expirationTime = bootstrapParams.expiresIn;
      const durationToExpire = moment(expirationTime).diff(now, 'second');

      if (durationToExpire < 0) {
        metricApiSvc.log(LogLevel.Debug, expireLogMessage);
        selectedExamDetailSvc.resetExamService();
        router.navigate(['/home']);
        selectedExamDetailSvc.sessionExpired = true;
        window.location.reload();
      } else {
        window.addEventListener('click', throttleResetTimer, false);
        //   // window.onscroll = resetTimer;    // catches scrolling
        window.addEventListener('keydown', throttleResetTimer, false);
      }
    }

    let postOnce: any;

    function resetTimer() {
      clearTimeout(t);
      let timeout = (parseInt(sessionTime)) * 1000;
      t = setTimeout(reload, timeout);  // time is in milliseconds (1000 is 1 second)
      sessionStorage.setItem('timeout', JSON.stringify(t));

      if(bootstrapParams.Token && bootstrapParams.expiresIn)
      {
        var now = moment();

        const token = bootstrapParams.Token;
        const expirationTime = bootstrapParams.expiresIn;
        const refreshPriorExp = bootstrapParams.RefreshPriorExp ? Math.floor(parseInt(bootstrapParams.RefreshPriorExp) / 60) : 2;

        const durationToExpire = moment(expirationTime).diff(now, 'minutes');

        if (!postOnce) {
          if (durationToExpire <= refreshPriorExp && durationToExpire > 0) {
            postOnce = true;
            utilitySvc.getNewToken(token)
              .subscribe((response: any) => {
                if (response) {
                  bootstrapParams.Token = response.token;
                  bootstrapParams.expiresIn = response.expire;

                  setTimeout(() => {
                    postOnce = false;
                  }, refreshPriorExp); // Reset flag to check token validity
                }
              });
            return;
          }
          else {
            window.removeEventListener('click', throttleResetTimer, false);
            window.removeEventListener('keydown', throttleResetTimer, false);
            window.removeEventListener('scroll', throttleResetTimer, false);
            window.removeEventListener('mousemove', throttleResetTimer, false);
            window.removeEventListener('mousedown', throttleResetTimer, false);
            window.removeEventListener('load', throttleResetTimer, false);

            window.addEventListener('click', refresh, false);
            window.addEventListener('keydown', refresh, false);
          }
        }
      }
    }

    /**
    * Throttle refresh timer, limit to one call per second
    */
    let timerThrottleFlag: boolean = false;
    function throttleResetTimer() {
      if (!timerThrottleFlag) {
        resetTimer();
        timerThrottleFlag = true;
        setTimeout(function () { timerThrottleFlag = false; }, 1000);
      }
    }
  }
}
